<script setup lang="ts">
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import {
  Activity,
  ArrowRight,
  Bot,
  CheckCircle2,
  ChevronDown,
  Clock3,
  Database,
  MessageSquareText,
  Radio,
  Sparkles,
  TriangleAlert,
  UsersRound,
} from 'lucide-vue-next'

import type {
  AnalysisResult,
  AnalysisSettings,
  Channel,
  MarketAlertEpisode,
  MarketStatus,
  MonitorStatus,
  TelegramMessage,
} from '@/types/monitor'
import { formatLocalDateTime, truncate } from '@/utils/format'

const props = defineProps<{
  status: MonitorStatus
  channels: Channel[]
  messages: TelegramMessage[]
  latestAnalysis: AnalysisResult | null
  analysisSettings: AnalysisSettings | null
  marketStatus: MarketStatus
  marketEpisodes: MarketAlertEpisode[]
  realtime: boolean
  canWrite: boolean
}>()

const emit = defineEmits<{
  navigate: [target: 'messages' | 'analysis' | 'sources']
  triggerAnalysis: []
}>()

const { t } = useI18n()
const latestMessages = computed(() => props.messages.slice(0, 7))
const recentChannels = computed(() =>
  [...props.channels]
    .sort((left, right) => Date.parse(right.last_message_at ?? '') - Date.parse(left.last_message_at ?? ''))
    .slice(0, 4),
)
const latestTopics = computed(() => Object.keys(props.latestAnalysis?.topic_summaries ?? {}).slice(0, 4))
const marketEpisodes = computed(() => props.marketEpisodes.slice(0, 8))
const resolvedEpisodes = computed(() => props.marketEpisodes.filter((episode) => episode.status === 'resolved').length)
const nextAnalysis = computed(() => {
  if (!props.analysisSettings?.enabled) return t('workspace.noSchedule')
  return props.status.analysis_next_run_at
    ? formatLocalDateTime(props.status.analysis_next_run_at)
    : t('workspace.neverRun')
})
const lastAnalysis = computed(() =>
  props.status.analysis_last_run_at
    ? formatLocalDateTime(props.status.analysis_last_run_at)
    : props.latestAnalysis?.created_at
      ? formatLocalDateTime(props.latestAnalysis.created_at)
      : t('workspace.neverRun'),
)

function episodeTypeLabel(episode: MarketAlertEpisode): string {
  return t(`marketEpisodes.types.${episode.alert_type}`)
}

function episodeWindow(episode: MarketAlertEpisode): string {
  if (episode.alert_type !== 'price_move' || episode.window_seconds <= 0) return ''
  if (episode.window_seconds % 60 === 0) return `${episode.window_seconds / 60}m`
  return `${episode.window_seconds}s`
}

function episodeValue(episode: MarketAlertEpisode): string {
  switch (episode.alert_type) {
    case 'price_move':
      return `${episode.current_value >= 0 ? '+' : ''}${episode.current_value.toFixed(2)}%`
    case 'spread_expansion':
      return `${episode.current_value.toFixed(0)} bps`
    case 'volume_spike':
      return `${episode.current_value.toFixed(1)}×`
    case 'trading_halt':
      return episode.status === 'open' ? t('marketEpisodes.halted') : t('marketEpisodes.resumed')
  }
}

function episodeDuration(episode: MarketAlertEpisode): string {
  const started = Date.parse(episode.started_at)
  const ended = episode.resolved_at ? Date.parse(episode.resolved_at) : Date.now()
  if (!Number.isFinite(started) || !Number.isFinite(ended) || ended < started) return '—'
  const minutes = Math.max(0, Math.round((ended - started) / 60_000))
  if (minutes < 60) return t('marketEpisodes.durationMinutes', { count: minutes })
  const hours = Math.floor(minutes / 60)
  const remainder = minutes % 60
  return remainder > 0
    ? t('marketEpisodes.durationHoursMinutes', { hours, minutes: remainder })
    : t('marketEpisodes.durationHours', { count: hours })
}
</script>

<template>
  <section class="tm-view tm-overview">
    <header class="tm-page-header">
      <div>
        <h1>{{ t('workspace.overviewTitle') }}</h1>
        <p>{{ t('workspace.overviewDesc') }}</p>
      </div>
      <div class="tm-health-pill" :class="{ 'tm-health-pill--ok': realtime }">
        <span></span>{{ realtime ? t('workspace.online') : t('workspace.offline') }}
      </div>
    </header>

    <div class="tm-stat-grid">
      <article class="tm-stat-card">
        <div class="tm-stat-icon"><UsersRound :size="20" /></div>
        <div><span>{{ t('workspace.monitoredSources') }}</span><strong>{{ channels.length }}</strong></div>
      </article>
      <article class="tm-stat-card">
        <div class="tm-stat-icon"><Database :size="20" /></div>
        <div><span>{{ t('workspace.storedMessages') }}</span><strong>{{ status.total_messages.toLocaleString() }}</strong></div>
      </article>
      <article class="tm-stat-card">
        <div class="tm-stat-icon"><Sparkles :size="20" /></div>
        <div><span>{{ t('workspace.pendingAnalysis') }}</span><strong>{{ status.analysis_pending_messages.toLocaleString() }}</strong></div>
      </article>
      <article class="tm-stat-card">
        <div class="tm-stat-icon"><Bot :size="20" /></div>
        <div><span>{{ t('workspace.analyzedMessages') }}</span><strong>{{ status.total_analyzed.toLocaleString() }}</strong></div>
      </article>
    </div>

    <div class="tm-schedule-card tm-overview-control-card">
      <div class="tm-schedule-item">
        <Clock3 :size="18" />
        <div><span>{{ t('workspace.nextAnalysis') }}</span><strong>{{ nextAnalysis }}</strong></div>
      </div>
      <div class="tm-schedule-item">
        <Bot :size="18" />
        <div><span>{{ t('workspace.lastAnalysis') }}</span><strong>{{ lastAnalysis }}</strong></div>
      </div>
      <button v-if="canWrite" class="tm-button tm-button--primary" :disabled="status.analysis_running" @click="emit('triggerAnalysis')">
        <Sparkles :size="17" />
        {{ status.analysis_running ? t('workspace.runningAnalysis') : t('workspace.runAnalysis') }}
      </button>
    </div>

    <article v-if="recentChannels.length" class="tm-overview-source-card">
      <header>
        <div><UsersRound :size="17" /><div><strong>{{ t('workspace.sourceActivity') }}</strong><span>{{ t('workspace.sourceActivityHint') }}</span></div></div>
        <button v-if="canWrite" class="tm-text-button" @click="emit('navigate', 'sources')">{{ t('workspace.manageSources') }}<ArrowRight :size="14" /></button>
      </header>
      <div class="tm-overview-source-list">
        <div v-for="channel in recentChannels" :key="channel.username">
          <span class="tm-overview-source-avatar">{{ (channel.title || channel.username || '?').slice(0, 1).toUpperCase() }}</span>
          <div><strong>{{ channel.title || channel.username }}</strong><span>{{ channel.last_message_at ? formatLocalDateTime(channel.last_message_at) : t('workspace.neverRun') }}</span></div>
          <strong>{{ channel.message_count.toLocaleString() }}</strong>
        </div>
      </div>
    </article>

    <article v-if="marketStatus.enabled || marketEpisodes.length" class="tm-overview-episode-card">
      <header>
        <div class="tm-overview-episode-title">
          <Activity :size="17" />
          <div>
            <strong>{{ t('marketEpisodes.title') }}</strong>
            <span>{{ t('marketEpisodes.hint') }}</span>
          </div>
        </div>
        <div class="tm-overview-episode-counters">
          <span class="is-open"><TriangleAlert :size="13" />{{ marketStatus.episodes_open }} {{ t('marketEpisodes.open') }}</span>
          <span><CheckCircle2 :size="13" />{{ resolvedEpisodes }} {{ t('marketEpisodes.resolvedLoaded') }}</span>
        </div>
      </header>

      <div v-if="marketEpisodes.length" class="tm-overview-episode-list">
        <details v-for="episode in marketEpisodes" :key="episode.id" class="tm-overview-episode-row">
          <summary>
            <span class="tm-episode-status" :class="`tm-episode-status--${episode.status}`">
              <TriangleAlert v-if="episode.status === 'open'" :size="14" />
              <CheckCircle2 v-else :size="14" />
              {{ episode.status === 'open' ? t('marketEpisodes.active') : t('marketEpisodes.recovered') }}
            </span>
            <span class="tm-episode-asset">
              <strong>{{ episode.symbol }}</strong>
              <small>{{ episodeTypeLabel(episode) }}<template v-if="episodeWindow(episode)"> · {{ episodeWindow(episode) }}</template></small>
            </span>
            <span class="tm-episode-value">
              <strong>{{ episodeValue(episode) }}</strong>
              <small>{{ episode.threshold_ratio.toFixed(2) }}× {{ t('marketEpisodes.threshold') }}</small>
            </span>
            <span class="tm-episode-duration">
              <strong>{{ episodeDuration(episode) }}</strong>
              <small>{{ episode.event_count }} {{ t('marketEpisodes.transitions') }}</small>
            </span>
            <span class="tm-episode-severity" :class="`tm-episode-severity--${episode.severity}`">
              {{ episode.severity.toUpperCase() }}
            </span>
            <ChevronDown class="tm-episode-chevron" :size="15" />
          </summary>
          <div class="tm-episode-timeline">
            <div v-for="event in episode.events" :key="event.id" class="tm-episode-timeline-item" :class="`is-${event.transition}`">
              <span class="tm-episode-timeline-dot"></span>
              <div>
                <strong>{{ t(`marketEpisodes.transitionsMap.${event.transition}`) }}</strong>
                <small>{{ formatLocalDateTime(event.observed_at) }}</small>
              </div>
              <span>{{ event.threshold_ratio.toFixed(2) }}×</span>
              <span>{{ event.severity.toUpperCase() }}</span>
            </div>
          </div>
        </details>
      </div>
      <div v-else class="tm-overview-episode-empty">
        <CheckCircle2 :size="18" />{{ t('marketEpisodes.empty') }}
      </div>
    </article>

    <div class="tm-dashboard-grid">
      <article class="tm-card tm-card--feed">
        <header class="tm-card-header">
          <div><MessageSquareText :size="19" /><h2>{{ t('workspace.latestMessages') }}</h2></div>
          <button class="tm-text-button" @click="emit('navigate', 'messages')">{{ t('workspace.viewAll') }}</button>
        </header>
        <div v-if="latestMessages.length" class="tm-message-list tm-message-list--compact">
          <article v-for="message in latestMessages" :key="message.id" class="tm-message-row">
            <div class="tm-message-avatar tm-message-avatar--sender">
              <span>{{ (message.sender_name || message.channel_name || '?').slice(0, 1).toUpperCase() }}</span>
              <img v-if="message.sender_avatar_url" :src="message.sender_avatar_url" :alt="message.sender_name || message.channel_name" loading="lazy" />
            </div>
            <div class="tm-message-body">
              <div class="tm-message-meta"><strong>{{ message.sender_name || message.channel_name }}</strong><span v-if="message.sender_username">@{{ message.sender_username }}</span><span class="tm-message-source">{{ message.channel_name }}</span><span>{{ formatLocalDateTime(message.published_at) }}</span></div>
              <p>{{ truncate(message.text || message.media_caption || '', 180) }}</p>
            </div>
          </article>
        </div>
        <div v-else class="tm-empty-state"><Radio :size="30" /><p>{{ t('workspace.noMessages') }}</p></div>
      </article>

      <article class="tm-card tm-card--analysis">
        <header class="tm-card-header">
          <div><Sparkles :size="19" /><h2>{{ t('workspace.latestAnalysis') }}</h2></div>
          <button class="tm-text-button" @click="emit('navigate', 'analysis')">{{ t('workspace.viewAll') }}</button>
        </header>
        <template v-if="latestAnalysis">
          <div class="tm-analysis-meta">
            <span class="tm-urgency" :class="`tm-urgency--${latestAnalysis.urgency.toLowerCase()}`">{{ latestAnalysis.urgency }}</span>
            <span>{{ latestAnalysis.message_count }} {{ t('workspace.messages').toLowerCase() }}</span>
            <span>{{ formatLocalDateTime(latestAnalysis.created_at) }}</span>
          </div>
          <p class="tm-analysis-summary">{{ latestAnalysis.summary }}</p>
          <p v-if="latestAnalysis.urgency_reason" class="tm-analysis-reason">{{ latestAnalysis.urgency_reason }}</p>
          <div v-if="latestTopics.length" class="tm-overview-topic-chips">
            <span v-for="topic in latestTopics" :key="topic">{{ topic }}</span>
          </div>
        </template>
        <div v-else class="tm-empty-state"><Bot :size="30" /><p>{{ t('workspace.noAnalysis') }}</p></div>
      </article>
    </div>
  </section>
</template>
